import React, { useState } from 'react';
import { 
  Lock, 
  Key, 
  ShieldCheck, 
  AlertTriangle, 
  CheckCircle2, 
  Eye, 
  EyeOff,
  Cpu
} from 'lucide-react';

export const PasswordAnalyzerPage: React.FC = () => {
  const [password, setPassword] = useState('');
  const [show, setShow] = useState(false);

  const calculateStrength = (pwd: string) => {
    if (!pwd) return { score: 0, label: 'Empty', crackTime: 'Instant', entropy: 0 };
    let score = 0;
    if (pwd.length >= 8) score += 20;
    if (pwd.length >= 14) score += 30;
    if (pwd.length >= 18) score += 20;
    if (/[A-Z]/.test(pwd)) score += 10;
    if (/[0-9]/.test(pwd)) score += 10;
    if (/[^A-Za-z0-9]/.test(pwd)) score += 10;

    let crackTime = '1 second';
    if (score >= 90) crackTime = '3.8 million years (on RTX 4090 GPU cluster)';
    else if (score >= 70) crackTime = '42 years';
    else if (score >= 50) crackTime = '3 days';
    else crackTime = 'Under 2 minutes';

    return {
      score: Math.min(100, score),
      label: score >= 80 ? 'EXCELLENT' : score >= 50 ? 'MODERATE' : 'WEAK',
      crackTime,
      entropy: Math.round(pwd.length * 4.2)
    };
  };

  const stat = calculateStrength(password);

  return (
    <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-10 space-y-8">
      <div className="text-center space-y-2">
        <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-emerald-950 text-emerald-300 text-xs font-bold border border-emerald-800">
          <Lock className="w-3.5 h-3.5" />
          <span>ZERO-KNOWLEDGE CLIENT-SIDE ENTROPY ANALYZER</span>
        </div>
        <h1 className="text-3xl font-extrabold text-white">Password Strength & GPU Crack Time Estimator</h1>
        <p className="text-xs text-slate-400 max-w-xl mx-auto">
          Test passphrases locally in your browser. Passwords are never sent to any server.
        </p>
      </div>

      <div className="bg-slate-900 border border-slate-800 rounded-3xl p-6 sm:p-8 space-y-6 shadow-xl text-xs">
        <div className="space-y-2">
          <label className="block font-bold text-slate-300">Test Password or 4-Word Passphrase:</label>
          <div className="relative">
            <input
              type={show ? 'text' : 'password'}
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              placeholder="e.g. kangaroo-bondi-sunset-paddle"
              className="w-full px-4 py-3 rounded-xl bg-slate-950 border border-slate-700 text-white font-mono text-sm focus:outline-none focus:border-emerald-500"
            />
            <button
              type="button"
              onClick={() => setShow(!show)}
              className="absolute right-3.5 top-1/2 -translate-y-1/2 text-slate-400 hover:text-white"
            >
              {show ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
            </button>
          </div>
        </div>

        {password && (
          <div className="p-6 rounded-2xl bg-slate-950 border border-slate-800 space-y-4 animate-fade-in">
            <div className="flex items-center justify-between">
              <span className="text-slate-400 uppercase font-mono">Password Strength:</span>
              <span className={`px-3 py-1 rounded-full font-bold text-xs ${
                stat.score >= 80 ? 'bg-emerald-950 text-emerald-300 border border-emerald-800' : 'bg-rose-950 text-rose-300 border-rose-800'
              }`}>
                {stat.label} ({stat.score}/100)
              </span>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 pt-2">
              <div className="p-3 rounded-xl bg-slate-900 border border-slate-800">
                <span className="text-slate-400 block mb-1">Estimated GPU Cluster Crack Time:</span>
                <span className="font-bold text-white font-mono">{stat.crackTime}</span>
              </div>
              <div className="p-3 rounded-xl bg-slate-900 border border-slate-800">
                <span className="text-slate-400 block mb-1">Entropy Metric:</span>
                <span className="font-bold text-emerald-400 font-mono">~{stat.entropy} bits of entropy</span>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};
